import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Header, Footer } from "@/pages/LandingPage";
import { useLang } from "@/i18n";
import { PatternDivider, KenteBorder } from "@/components/EthnicPatterns";
import {
  Target, Eye, HeartHandshake, Sparkles, Globe2, ShieldCheck,
  Users, Mic, FileText, Check,
} from "lucide-react";

export default function AboutPage() {
  const { t } = useLang();
  const a = t.about;
  const valueIcons = [Sparkles, ShieldCheck, HeartHandshake, Globe2];
  const values = a.values.map((v, i) => ({ ...v, icon: valueIcons[i] }));
  const whatIcons = [Mic, FileText, Sparkles, Users, Check, ShieldCheck];
  const whatItems = a.whatItems.map((w, i) => ({ t: w, icon: whatIcons[i] }));
  return (
    <div className="min-h-screen bg-af">
      <Header />

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-16 md:pt-24 pb-12 text-center">
        <PatternDivider />
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-4" data-testid="about-title">
          {a.title1}<span className="text-terracotta">AfriMeet</span>
        </h1>
        <p className="mt-6 text-base md:text-lg text-af-secondary leading-relaxed max-w-2xl mx-auto">
          {a.intro}
        </p>
      </section>

      {/* Story */}
      <section className="bg-[#F5EFD8] border-y border-af">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="font-display font-extrabold text-2xl md:text-3xl mb-6">{a.storyTitle}</h2>
          <div className="space-y-4 text-af-secondary leading-relaxed text-sm md:text-base" data-testid="about-story">
            {a.story.map((p) => (
              <p key={p.slice(0, 40)}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-white border border-af rounded-md p-8" data-testid="about-mission">
            <div className="h-12 w-12 rounded-md bg-[#F5EFD8] border border-af flex items-center justify-center mb-5">
              <Target className="h-6 w-6 text-terracotta" />
            </div>
            <h3 className="font-display font-bold text-xl mb-3">{a.missionTitle}</h3>
            <p className="text-sm md:text-base text-af-secondary leading-relaxed">
              {a.missionText}
            </p>
          </div>
          <div className="bg-[#2B1F12] text-white border border-[#2B1F12] rounded-md p-8" data-testid="about-vision">
            <div className="h-12 w-12 rounded-md bg-white/10 border border-white/15 flex items-center justify-center mb-5">
              <Eye className="h-6 w-6 text-[#D4A04E]" />
            </div>
            <h3 className="font-display font-bold text-xl mb-3">{a.visionTitle}</h3>
            <p className="text-sm md:text-base text-white/80 leading-relaxed">
              {a.visionText}
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#F5EFD8] border-y border-af">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center mb-3"><PatternDivider /></div>
          <h2 className="font-display font-extrabold text-2xl md:text-3xl text-center mb-12">{a.valuesTitle}</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {values.map((v, i) => (
              <div key={v.t} className="bg-white border border-af rounded-md p-7 flex gap-4" data-testid={`about-value-${i}`}>
                <div className="h-11 w-11 rounded-md bg-[#F5EFD8] border border-af flex items-center justify-center shrink-0">
                  <v.icon className="h-5 w-5 text-terracotta" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-base mb-1.5">{v.t}</h3>
                  <p className="text-sm text-af-secondary leading-relaxed">{v.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {a.stats.map((s) => (
            <div key={s.l} className="text-center bg-white border border-af rounded-md p-6">
              <div className="font-display font-extrabold text-2xl md:text-3xl text-terracotta">{s.v}</div>
              <div className="text-xs md:text-sm text-af-secondary mt-2 leading-snug">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* What we do */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="bg-white border border-af rounded-md p-8 md:p-10">
          <h2 className="font-display font-extrabold text-2xl md:text-3xl mb-6">{a.whatTitle}</h2>
          <ul className="grid md:grid-cols-2 gap-x-8 gap-y-3">
            {whatItems.map((f) => (
              <li key={f.t} className="flex items-start gap-3 text-sm md:text-base text-af-secondary">
                <f.icon className="h-4 w-4 text-terracotta shrink-0 mt-1" /> {f.t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#2B1F12] text-white relative">
        <KenteBorder className="absolute top-0 left-0 right-0 h-1.5" />
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="font-display font-extrabold text-2xl md:text-4xl mb-4">
            {a.ctaTitle}
          </h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">
            {a.ctaText}
          </p>
          <Link to="/auth/signup" data-testid="about-cta-signup">
            <Button size="lg" className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md h-12 px-8 font-semibold">
              {a.ctaBtn}
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
